import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { validateJournalBalance, type JournalLine } from '@finvox/shared/journal';
import { roundMoney, parseMoneyInput } from '@finvox/shared/money';
import { getFirestoreDb } from '@/lib/firebase';
import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiPost } from '@/lib/api';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { useToast } from '@/contexts/ToastContext';
import { mapJournalBalanceError, toastError } from '@/lib/toastErrors';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

type Account = { id: string; code: string; name: string };

const emptyLine = (): JournalLine => ({
  accountId: '',
  debit: '0.00',
  credit: '0.00'
});

export function JournalEditorPage() {
  const { t } = useTranslation();
  const { orgId, currentPeriodId, periods, skipLiveUpdate } = useOrg();
  const { entryId } = useParams();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const toast = useToast();
  const isNew = entryId === 'new';
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [costCenters, setCostCenters] = useState<{ id: string; name: string }[]>([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<JournalLine[]>([emptyLine(), emptyLine()]);
  const [status, setStatus] = useState<'draft' | 'posted'>('draft');
  const [saving, setSaving] = useState(false);
  const periodOpen = periods.find((p) => p.id === currentPeriodId)?.status === 'open';

  const balance = useMemo(() => validateJournalBalance(lines), [lines]);

  useEffect(() => {
    if (!orgId) return;
    const db = getFirestoreDb();
    const unsubCc = onSnapshot(collection(db, 'organizations', orgId, 'costCenters'), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      setCostCenters(snap.docs.map((d) => ({ id: d.id, name: String(d.data().name ?? '') })));
    });
    const unsubAcc = onSnapshot(collection(db, 'organizations', orgId, 'accounts'), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      setAccounts(
        snap.docs
          .map((d) => ({ id: d.id, code: d.data().code, name: d.data().name }))
          .sort((a, b) => a.code.localeCompare(b.code))
      );
    });
    return () => {
      unsubCc();
      unsubAcc();
    };
  }, [orgId, skipLiveUpdate]);

  useEffect(() => {
    if (!orgId || isNew) return;
    const db = getFirestoreDb();
    return onSnapshot(doc(db, 'organizations', orgId, 'journalEntries', entryId!), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      if (!snap.exists()) return;
      const d = snap.data();
      setDate(String(d.date ?? date));
      setMemo(String(d.memo ?? ''));
      setLines((d.lines as JournalLine[]) ?? [emptyLine()]);
      setStatus(d.status === 'posted' ? 'posted' : 'draft');
    });
  }, [orgId, entryId, isNew, date, skipLiveUpdate]);

  const persistDraft = useCallback(async () => {
    if (!orgId || !currentPeriodId || !online || status === 'posted') return;
    setSaving(true);
    const payload = {
      date,
      memo,
      periodId: currentPeriodId,
      lines: lines.map((l) => ({
        ...l,
        debit: roundMoney(parseMoneyInput(l.debit)),
        credit: roundMoney(parseMoneyInput(l.credit))
      })),
      status: 'draft',
      updatedAt: serverTimestamp()
    };
    const db = getFirestoreDb();
    try {
      if (isNew) {
        const ref = doc(collection(db, 'organizations', orgId, 'journalEntries'));
        await setDoc(ref, { ...payload, createdAt: serverTimestamp(), createdBy: '' });
        navigate(`/journals/${ref.id}`, { replace: true });
      } else {
        await updateDoc(doc(db, 'organizations', orgId, 'journalEntries', entryId!), payload);
      }
    } catch (e) {
      toastError(toast, e, 'journal', 'journals.saveFailed');
    } finally {
      setSaving(false);
    }
  }, [orgId, currentPeriodId, online, status, date, memo, lines, isNew, entryId, navigate, toast]);

  useEffect(() => {
    if (!online || status === 'posted') return;
    const tmr = setTimeout(() => void persistDraft(), 800);
    return () => clearTimeout(tmr);
  }, [date, memo, lines, persistDraft, online, status]);

  async function postEntry() {
    if (!orgId || !entryId || isNew || !balance.valid) return;
    await persistDraft();
    try {
      await apiPost('/v1/journals/post', { orgId, entryId });
      navigate('/journals');
    } catch (e) {
      toastError(toast, e, 'journal', 'journals.postFailed');
    }
  }

  function updateLine(i: number, patch: Partial<JournalLine>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">
        {isNew ? t('journals.new') : t('journals.edit')}
      </h1>
      {status === 'posted' && (
        <p className="mt-2 text-sm text-[var(--fv-primary)]">{t('journals.postedReadonly')}</p>
      )}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="text-sm">
          {t('journals.date')}
          <DatePicker
            className="mt-1"
            value={date}
            onChange={setDate}
            disabled={!online || status === 'posted'}
          />
        </label>
        <label className="text-sm md:col-span-2">
          {t('journals.memo')}
          <Input
            className="mt-1"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            disabled={!online || status === 'posted'}
          />
        </label>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-[var(--fv-bg)]">
            <tr>
              <th className="p-2">{t('coa.code')}</th>
              <th className="p-2">{t('settings.costCenterName')}</th>
              <th className="p-2 text-end">{t('coa.debit')}</th>
              <th className="p-2 text-end">{t('coa.credit')}</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">
                  <Select
                    className="w-full min-w-[10rem]"
                    value={line.accountId}
                    disabled={!online || status === 'posted'}
                    placeholder="—"
                    onValueChange={(id) => {
                      const acc = accounts.find((a) => a.id === id);
                      updateLine(i, { accountId: id, accountCode: acc?.code });
                    }}
                    options={[
                      { value: '', label: '—' },
                      ...accounts.map((a) => ({
                        value: a.id,
                        label: `${a.code} — ${a.name}`
                      }))
                    ]}
                  />
                </td>
                <td className="p-2">
                  <Select
                    className="w-full min-w-[8rem]"
                    value={line.costCenterId ?? ''}
                    disabled={!online || status === 'posted'}
                    placeholder="—"
                    onValueChange={(id) =>
                      updateLine(i, { costCenterId: id || undefined })
                    }
                    options={[
                      { value: '', label: '—' },
                      ...costCenters.map((c) => ({ value: c.id, label: c.name }))
                    ]}
                  />
                </td>
                <td className="p-2">
                  <MoneyInput
                    className="w-full"
                    disabled={!online || status === 'posted'}
                    value={line.debit}
                    onValueChange={(debit) =>
                      updateLine(i, { debit: debit || '0.00', credit: '0.00' })
                    }
                  />
                </td>
                <td className="p-2">
                  <MoneyInput
                    className="w-full"
                    disabled={!online || status === 'posted'}
                    value={line.credit}
                    onValueChange={(credit) =>
                      updateLine(i, { credit: credit || '0.00', debit: '0.00' })
                    }
                  />
                </td>
                <td className="p-2">
                  {status === 'draft' && lines.length > 2 && (
                    <IconButton
                      size="sm"
                      variant="danger"
                      aria-label={t('common.delete')}
                      onClick={() => setLines((p) => p.filter((_, j) => j !== i))}
                    >
                      ×
                    </IconButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {status === 'draft' && (
        <Button
          variant="secondary"
          className="mt-3"
          onClick={() => setLines((p) => [...p, emptyLine()])}
          disabled={!online}
        >
          {t('journals.addLine')}
        </Button>
      )}

      <div
        className={`mt-4 rounded p-3 text-sm ${balance.valid ? 'bg-[var(--fv-primary-muted)]' : 'bg-red-50 text-[var(--fv-danger)]'}`}
      >
        {t('journals.debitTotal')}: <MoneyDisplay value={balance.debitTotal} /> · {t('journals.creditTotal')}:{' '}
        <MoneyDisplay value={balance.creditTotal} />
        {!balance.valid &&
          balance.error &&
          ` — ${mapJournalBalanceError(balance.error)}`}
      </div>

      {saving && <p className="mt-2 text-sm text-[var(--fv-text-muted)]">{t('journals.saving')}</p>}

      <div className="mt-6 flex gap-3">
        {status === 'draft' && (
          <Button
            disabled={!online || !balance.valid || !periodOpen || isNew}
            onClick={() => void postEntry()}
          >
            {t('journals.post')}
          </Button>
        )}
        <Button variant="secondary" onClick={() => navigate('/journals')}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
